import { AxiosResponse, AxiosError } from 'axios'
import request from '@/axios/config'
import {
  Notification,
  NotificationListParams,
  NotificationListResponse,
  UnreadCountResponse,
  MarkAsReadRequest,
  MarkAsReadResponse,
  MarkAllAsReadResponse,
  DeleteNotificationResponse,
  NotificationApiError,
  NetworkErrorType,
  NotificationError,
} from '@/pages/NotificationCenter/types'

// ================================
// CONSTANTS
// ================================

const NOTIFICATION_BASE_URL = '/v1/notification-center'

const DEFAULT_RETRY_CONFIG = {
  maxRetries: 3,
  retryDelay: 1000, // 1 second
  retryMultiplier: 2, // Exponential backoff
}

const REQUEST_TIMEOUT = 10000 // 10 seconds

// ================================
// CACHE CONFIGURATION
// ================================

const CACHE_CONFIG = {
  defaultTTL: 30000, // 30 seconds
  maxCacheSize: 100, // Maximum number of cached items
  unreadCountTTL: 10000, // 10 seconds for unread count
}

// ================================
// DATE NORMALIZATION UTILITIES
// ================================

/**
 * Normalizes a date string from PostgreSQL format to ISO 8601 format
 * PostgreSQL: "2025-09-26 22:45:33.514+00"
 * ISO 8601: "2025-09-26T22:45:33.514Z"
 */
function normalizeDateString(dateString: string | null): string | null {
  if (!dateString) return null

  // If already in ISO format, return as is
  if (dateString.includes('T') && dateString.includes('Z')) {
    return dateString
  }

  // Convert PostgreSQL format to ISO format
  // Replace space with 'T' and '+00' with 'Z'
  return dateString.replace(' ', 'T').replace('+00', 'Z')
}

/**
 * Normalizes all date fields in a notification object
 */
function normalizeNotificationDates(notification: Notification): Notification {
  return {
    ...notification,
    created_at:
      normalizeDateString(notification.created_at) || notification.created_at,
    expires_at:
      normalizeDateString(notification.expires_at) || notification.expires_at,
    read_at: normalizeDateString(notification.read_at),
  }
}

interface CacheEntry<T> {
  data: T
  timestamp: number
  ttl: number
}

// ================================
// ERROR HANDLING UTILITIES
// ================================

/**
 * Maps HTTP status codes to network error types
 */
function getNetworkErrorType(status?: number): NetworkErrorType {
  if (!status) return NetworkErrorType.NETWORK_ERROR

  switch (status) {
    case 401:
      return NetworkErrorType.UNAUTHORIZED
    case 403:
      return NetworkErrorType.FORBIDDEN
    case 404:
      return NetworkErrorType.NOT_FOUND
    case 408:
      return NetworkErrorType.TIMEOUT
    case 429:
      return NetworkErrorType.RATE_LIMITED
    case 500:
    case 502:
    case 503:
    case 504:
      return NetworkErrorType.SERVER_ERROR
    default:
      return NetworkErrorType.UNKNOWN
  }
}

/**
 * Creates a standardized notification error
 */
function createNotificationError(
  message: string,
  code: string,
  originalError?: Error,
  status?: number,
  retryable = false
): NotificationError {
  const error = new Error(message) as NotificationError
  error.name = 'NotificationError'
  error.code = code
  error.retryable = retryable
  error.status = status
  error.originalError = originalError

  return error
}

/**
 * Determines if an error type is retryable
 */
function isRetryableErrorType(errorType: NetworkErrorType): boolean {
  const retryableTypes = [
    NetworkErrorType.TIMEOUT,
    NetworkErrorType.NETWORK_ERROR,
    NetworkErrorType.RATE_LIMITED,
    NetworkErrorType.SERVER_ERROR,
  ]

  return retryableTypes.includes(errorType)
}

/**
 * Handles axios errors and converts them to standardized notification errors
 */
function handleApiError(error: unknown): NotificationError {
  if (error instanceof AxiosError) {
    const status = error.response?.status
    const errorType = getNetworkErrorType(status)
    const responseData = error.response?.data as
      | NotificationApiError
      | undefined

    // Use API error message if available, otherwise use a generic message
    const message =
      responseData?.message || error.message || 'An unexpected error occurred'
    const code = responseData?.code || errorType
    const retryable = responseData?.retryable ?? isRetryableErrorType(errorType)

    return createNotificationError(message, code, error, status, retryable)
  }

  // Handle non-axios errors
  if (error instanceof Error) {
    return createNotificationError(
      error.message,
      NetworkErrorType.UNKNOWN,
      error,
      undefined,
      false
    )
  }

  // Handle unknown errors
  return createNotificationError(
    'An unknown error occurred',
    NetworkErrorType.UNKNOWN,
    undefined,
    undefined,
    false
  )
}

// ================================
// RETRY LOGIC
// ================================

/**
 * Implements exponential backoff delay
 */
function calculateDelay(
  attempt: number,
  baseDelay: number,
  multiplier: number
): number {
  return baseDelay * multiplier ** (attempt - 1)
}

/**
 * Delays execution for the specified number of milliseconds
 */
function delay(ms: number): Promise<void> {
  return new Promise<void>(resolve => {
    setTimeout(resolve, ms)
  })
}

/**
 * Retries a function with exponential backoff
 */
async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  maxRetries: number = DEFAULT_RETRY_CONFIG.maxRetries,
  baseDelay: number = DEFAULT_RETRY_CONFIG.retryDelay,
  multiplier: number = DEFAULT_RETRY_CONFIG.retryMultiplier
): Promise<T> {
  let lastError: NotificationError | undefined

  for (let attempt = 1; attempt <= maxRetries + 1; attempt++) {
    try {
      // eslint-disable-next-line no-await-in-loop
      return await fn()
    } catch (error) {
      const notificationError = handleApiError(error)
      lastError = notificationError

      // Don't retry if the error is not retryable or it's the last attempt
      if (!notificationError.retryable || attempt > maxRetries) {
        throw notificationError
      }

      // Calculate delay and wait before retrying
      const delayMs = calculateDelay(attempt, baseDelay, multiplier)
      // eslint-disable-next-line no-await-in-loop
      await delay(delayMs)
    }
  }

  // This should never be reached due to the loop logic, but TypeScript needs it
  if (lastError) {
    throw lastError
  }
  throw new Error('Retry loop completed without error or result')
}

// ================================
// API SERVICE METHODS
// ================================

/**
 * Notification API Service Class
 *
 * Provides methods for all notification-related API operations including:
 * - Fetching notifications with pagination and filtering
 * - Managing read/unread status
 * - Deleting notifications
 * - Getting unread counts
 * - Pusher authentication
 */
export class NotificationApiService {
  // ================================
  // CACHE MANAGEMENT
  // ================================

  private cache = new Map<string, CacheEntry<unknown>>()

  /**
   * Generate cache key from method and parameters
   */
  private getCacheKey(method: string, params?: unknown): string {
    if (params) {
      return `${method}:${JSON.stringify(params)}`
    }
    return method
  }

  /**
   * Check if cache entry is valid (not expired)
   */
  private isCacheValid<T>(entry: CacheEntry<T>): boolean {
    return Date.now() - entry.timestamp < entry.ttl
  }

  /**
   * Get data from cache if available and valid
   */
  private getFromCache<T>(key: string): T | null {
    const entry = this.cache.get(key)
    if (entry && this.isCacheValid(entry)) {
      return entry.data as T
    }
    if (entry) {
      // Entry exists but is expired, remove it
      this.cache.delete(key)
    }
    return null
  }

  /**
   * Store data in cache with TTL
   */
  private setCache<T>(
    key: string,
    data: T,
    ttl: number = CACHE_CONFIG.defaultTTL
  ): void {
    // Enforce cache size limit
    if (this.cache.size >= CACHE_CONFIG.maxCacheSize) {
      // Remove oldest entries (simple FIFO strategy)
      const firstKey = this.cache.keys().next().value
      if (firstKey) {
        this.cache.delete(firstKey)
      }
    }

    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl,
    })
  }

  /**
   * Clear cache entries that match a pattern
   */
  private invalidateCache(pattern: string): void {
    const keysToDelete = Array.from(this.cache.keys()).filter(key =>
      key.includes(pattern)
    )
    keysToDelete.forEach(key => this.cache.delete(key))
  }

  /**
   * Clear all cache
   */
  clearCache(): void {
    this.cache.clear()
  }

  // ================================
  // API METHODS WITH CACHING
  // ================================
  /**
   * Fetches a paginated list of notifications with optional filtering and sorting
   *
   * @param params - Query parameters for filtering, sorting, and pagination
   * @returns Promise resolving to notification list response
   * @throws NotificationError on API errors
   *
   * @example
   * ```typescript
   * const response = await notificationApi.getNotifications({
   *   page: 1,
   *   per_page: 20,
   *   status: 'unread',
   *   sort: 'created_at',
   *   order: 'desc'
   * })
   * ```
   */
  async getNotifications(
    params?: NotificationListParams
  ): Promise<NotificationListResponse> {
    const cacheKey = this.getCacheKey('getNotifications', params)

    // Check cache first
    const cached = this.getFromCache<NotificationListResponse>(cacheKey)
    if (cached) {
      return cached
    }

    return retryWithBackoff(async () => {
      try {
        // Ensure sort parameter is always included (backend requires it despite OpenAPI spec)
        const requestParams = {
          sort: 'created_at',
          order: 'desc',
          ...params,
        }

        const response: AxiosResponse<NotificationListResponse> =
          await request.get(`${NOTIFICATION_BASE_URL}/`, {
            params: requestParams,
            timeout: REQUEST_TIMEOUT,
          })

        // Normalize date formats in all notifications
        const normalizedData = {
          ...response.data,
          notifications: response.data.notifications.map(
            normalizeNotificationDates
          ),
        }

        // Cache the normalized response
        this.setCache(cacheKey, normalizedData, CACHE_CONFIG.defaultTTL)

        return normalizedData
      } catch (error) {
        throw handleApiError(error)
      }
    })
  }

  /**
   * Fetches a specific notification by ID
   *
   * @param id - Notification ID (UUID)
   * @returns Promise resolving to the notification
   * @throws NotificationError on API errors or if not found
   *
   * @example
   * ```typescript
   * const notification = await notificationApi.getNotificationById('123e4567-e89b-12d3-a456-426614174000')
   * ```
   */
  async getNotificationById(id: string): Promise<Notification> {
    const cacheKey = this.getCacheKey('getNotificationById', id)

    // Check cache first
    const cached = this.getFromCache<Notification>(cacheKey)
    if (cached) {
      return cached
    }

    return retryWithBackoff(async () => {
      try {
        const response: AxiosResponse<Notification> = await request.get(
          `${NOTIFICATION_BASE_URL}/${id}`,
          {
            timeout: REQUEST_TIMEOUT,
          }
        )

        // Cache the response
        this.setCache(cacheKey, response.data, CACHE_CONFIG.defaultTTL)

        return response.data
      } catch (error) {
        throw handleApiError(error)
      }
    })
  }

  /**
   * Gets the current unread notification count
   *
   * @returns Promise resolving to unread count response
   * @throws NotificationError on API errors
   *
   * @example
   * ```typescript
   * const { unread_count } = await notificationApi.getUnreadCount()
   * console.log(`You have ${unread_count} unread notifications`)
   * ```
   */
  async getUnreadCount(): Promise<UnreadCountResponse> {
    const cacheKey = this.getCacheKey('getUnreadCount')

    // Check cache first
    const cached = this.getFromCache<UnreadCountResponse>(cacheKey)
    if (cached) {
      return cached
    }

    return retryWithBackoff(async () => {
      try {
        const response: AxiosResponse<UnreadCountResponse> = await request.get(
          `${NOTIFICATION_BASE_URL}/unread-count`,
          {
            timeout: REQUEST_TIMEOUT,
          }
        )

        // Cache the response with shorter TTL
        this.setCache(cacheKey, response.data, CACHE_CONFIG.unreadCountTTL)

        return response.data
      } catch (error) {
        throw handleApiError(error)
      }
    })
  }

  /**
   * Marks a specific notification as read
   *
   * @param id - Notification ID to mark as read
   * @param readAt - Optional timestamp for when it was read (defaults to server time)
   * @returns Promise resolving to the updated notification
   * @throws NotificationError on API errors
   *
   * @example
   * ```typescript
   * const updated = await notificationApi.markAsRead('123e4567-e89b-12d3-a456-426614174000')
   * console.log('Marked as read:', updated.notification.title)
   * ```
   */
  async markAsRead(id: string, readAt?: string): Promise<MarkAsReadResponse> {
    const result = await retryWithBackoff(async () => {
      try {
        const requestBody: MarkAsReadRequest = {}
        if (readAt) {
          requestBody.read_at = readAt
        }

        const response: AxiosResponse<MarkAsReadResponse> = await request.put(
          `${NOTIFICATION_BASE_URL}/${id}/read`,
          requestBody,
          {
            timeout: REQUEST_TIMEOUT,
          }
        )

        return normalizeNotificationDates(response.data)
      } catch (error) {
        throw handleApiError(error)
      }
    })

    // Invalidate relevant caches
    this.invalidateCache('getNotifications')
    this.invalidateCache('getUnreadCount')
    this.invalidateCache(`getNotificationById:${id}`)

    return result
  }

  /**
   * Marks all notifications as read
   *
   * @returns Promise resolving to bulk operation result
   * @throws NotificationError on API errors
   *
   * @example
   * ```typescript
   * const result = await notificationApi.markAllAsRead()
   * console.log(`${result.message} (${result.count} notifications)`)
   * ```
   */
  async markAllAsRead(): Promise<MarkAllAsReadResponse> {
    const result = await retryWithBackoff(async () => {
      try {
        const response: AxiosResponse<MarkAllAsReadResponse> =
          await request.put(
            `${NOTIFICATION_BASE_URL}/read-all`,
            {},
            {
              timeout: REQUEST_TIMEOUT,
            }
          )

        return response.data
      } catch (error) {
        throw handleApiError(error)
      }
    })

    // Invalidate all relevant caches since this affects multiple notifications
    this.invalidateCache('getNotifications')
    this.invalidateCache('getUnreadCount')
    this.invalidateCache('getNotificationById') // This will match all notification detail caches

    return result
  }

  /**
   * Deletes a specific notification
   *
   * @param id - Notification ID to delete
   * @returns Promise resolving to deletion result
   * @throws NotificationError on API errors
   *
   * @example
   * ```typescript
   * const result = await notificationApi.deleteNotification('123e4567-e89b-12d3-a456-426614174000')
   * if (result.success) {
   *   console.log('Notification deleted successfully')
   * }
   * ```
   */
  async deleteNotification(id: string): Promise<DeleteNotificationResponse> {
    const result = await retryWithBackoff(async () => {
      try {
        const response: AxiosResponse<DeleteNotificationResponse> =
          await request.delete(`${NOTIFICATION_BASE_URL}/${id}`, {
            timeout: REQUEST_TIMEOUT,
          })

        return response.data
      } catch (error) {
        throw handleApiError(error)
      }
    })

    // Invalidate relevant caches
    this.invalidateCache('getNotifications')
    this.invalidateCache('getUnreadCount')
    this.invalidateCache(`getNotificationById:${id}`)

    return result
  }

  /**
   * Authenticates with Pusher for real-time notifications
   *
   * @param socketId - Pusher socket ID
   * @param channelName - Channel name to authenticate for
   * @returns Promise resolving to authentication response
   * @throws NotificationError on API errors
   *
   * @example
   * ```typescript
   * const auth = await notificationApi.authenticatePusher('123.456', 'private-user-789')
   * // Use auth.auth and auth.channel_data with Pusher
   * ```
   */
  async authenticatePusher(
    socketId: string,
    channelName: string
  ): Promise<{
    auth: string
    channel_data?: string
  }> {
    return retryWithBackoff(async () => {
      try {
        const response = await request.post(
          `${NOTIFICATION_BASE_URL}/pusher/auth`,
          {
            socket_id: socketId,
            channel_name: channelName,
          },
          {
            timeout: REQUEST_TIMEOUT,
          }
        )

        return response.data
      } catch (error) {
        throw handleApiError(error)
      }
    })
  }
}

// ================================
// SINGLETON INSTANCE
// ================================

/**
 * Singleton instance of the notification API service
 *
 * Use this instance throughout the application for all notification API calls.
 * It ensures consistent configuration and behavior across the entire app.
 *
 * @example
 * ```typescript
 * import { notificationApi } from '@/services/notificationApi'
 *
 * // Fetch notifications
 * const notifications = await notificationApi.getNotifications()
 *
 * // Mark as read
 * await notificationApi.markAsRead(notificationId)
 * ```
 */
export const notificationApi = new NotificationApiService()

// ================================
// DEFAULT EXPORT
// ================================

/**
 * Default export for the notification API service
 */
export default notificationApi
